<i18n src="@/locales/modules/RequestTranslate.json"></i18n>

<template>
	<div class="request-filter">
		<div class="request-filter__item">
			<legend class="uppercase-first-letter text-solid-gray-1">{{ $t("RequestType") }}:</legend>
			<v-select
				label="text"
				class="w-full"
				ref="selectRequestTypeRef"
				:value="value.type"
				:options="localRequestTypes"
				:disabled="isDisabledFilterType"
				:clearable="!isDisabledFilterType"
				:placeholder="`${$t('Actions.Choose')} ${$t('RequestType').toLowerCase()}`"
				@input="type => $emit('onFilter', { ...value, type })"
				@search:focus="handleOnFocusRequestTypeSelect"
			>
				<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
			</v-select>
		</div>

		<div class="request-filter__item">
			<legend class="uppercase-first-letter text-solid-gray-1">{{ $t("Creator") }}:</legend>
			<v-select
				multiple
				label="text"
				ref="selectEmployeesRef"
				:value="value.creator"
				:options="parsedEmployees"
				class="select-multiple w-full"
				:placeholder="`${$t('Actions.Choose')} ${$t('Creator').toLowerCase()}`"
				@input="creator => $emit('onFilter', { ...value, creator })"
				@search:focus="handleOnFocusEmployeeSelect"
			>
				<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
			</v-select>
		</div>

		<div class="request-filter__item">
			<legend class="uppercase-first-letter text-solid-gray-1">{{ $t("Status") }}:</legend>
			<v-select
				multiple
				label="name"
				:value="value.status"
				:options="listCountMixin"
				class="select-multiple w-full"
				:placeholder="`${$t('Actions.Choose')} ${$t('Status').toLowerCase()}`"
				@input="status => $emit('onFilter', { ...value, status })"
				:reduce="status => status.id"
			>
				<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
			</v-select>
		</div>

		<div class="request-filter__item">
			<legend class="uppercase-first-letter text-solid-gray-1">{{ $t("FlagColor") }}:</legend>
			<v-select
				label="name"
				:value="value.value"
				:options="listFlagMixin"
				class="select-multiple w-full"
				:placeholder="`${$t('Actions.Choose')} ${$t('FlagColor').toLowerCase()}`"
				@input="flagColor => $emit('onFilter', { ...value, flagColor })"
				:reduce="flagColor => flagColor.value"
			>
				<template slot="selected-option" slot-scope="option">
					<div class="flex">
						<div class="col">
							<span class="fa" :class="option.icon"></span>
							<span>{{ option.value }}</span>
						</div>
					</div>
					</template>
					<template slot="option" slot-scope="option">
						<div>
							<div class="h-8 w-8 rounded" :style="{ backgroundColor: option.value }"></div>
							<span :style="{color: '#000000'}">{{ option.value }}</span>
						</div>
					</template>
				<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
			</v-select>
		</div>
		<!-- Department -->
		<div class="mb-6">
			<legend class="uppercase-first-letter text-solid-gray-1">{{ $t("Department") }}:</legend>
			<v-select
				:clearable="false"
				class="border-none w-full select-multiple"
				:placeholder="`${$t('Actions.Choose')} ${$t('Department').toLowerCase()}`"
				v-model="value.department_ids"
				name="type"
				:options="$store.state.departments"
				multiple
				:reduce="type => type.id"
				label="name"
			>
			</v-select>
		</div>

		<div class="request-filter__item">
			<legend class="uppercase-first-letter text-solid-gray-1">{{ $t("CreatedDate") }}:</legend>
			<cbs-date-range-picker
				type="datetime"
				prefix-icon="''"
				format="dd/MM/yyyy HH:mm"
				value-format="yyyy-MM-dd HH:mm"
				:value="value.createdDate"
				:toPlaceholder="`${$t('Actions.Choose')} ${$t('ToDate').toLowerCase()}`"
				:fromPlaceholder="`${$t('Actions.Choose')} ${$t('FromDate').toLowerCase()}`"
				@change="createdDate => $emit('onFilter', { ...value, createdDate })"
			/>
		</div>
	</div>
</template>

<script>
import _ from "lodash";
import requestGQL from "@/http/requests/request/request_v2_gql";
import employeeInsideGQL from "@/http/requests/inside/employee_gql";
import requestBasicHandlers from "@/mixins/requestBasicHandlers";

import vSelect from "vue-select";

export default {
	name: "RequestFilter",
	inheritAttrs: false,
	mixins: [requestBasicHandlers],

	components: {
		vSelect
	},
	props: {
		value: {
			type: Object,
			required: true,
			validator: prop => _.has(prop, "type") && _.has(prop, "status") && _.has(prop, "creator") && _.has(prop, "createdDate")
		},
		isDisabledFilterType: Boolean
	},
	emits: {
		onFilter: {}
	},
	data() {
		return {
			employees: [],
			requestTypes: []
		};
	},
	computed: {
		localRequestTypes() {
			const requestTypes = this.requestTypes || [];
			return requestTypes.map(type => ({ text: type.name, id: type.id, controls: type.controls }));
		},

		parsedEmployees() {
			return this.employees.map(employee => ({ text: employee.name, id: employee.id }));
		}
	},
	methods: {
		async handleOnFocusEmployeeSelect() {
			if (!this.value.creator || _.isEmpty(this.value.creator)) {
				const loadingRef = this.$refs.selectEmployeesRef && this.$refs.selectEmployeesRef.toggleLoading;
				try {
					if (loadingRef) {
						loadingRef(true);
					}
					const response = await employeeInsideGQL.fetchEmployees({});
					this.employees = response.employees || [];
				} catch (error) {
					this.$helper.notifyError({ context: this, error });
				} finally {
					if (loadingRef) {
						loadingRef(false);
					}
				}
			}
		},
		async handleOnFocusRequestTypeSelect() {
			if (!this.value.type || _.isEmpty(this.value.type)) {
				const loadingRef = this.$refs.selectRequestTypeRef && this.$refs.selectRequestTypeRef.toggleLoading;
				try {
					if (loadingRef) {
						loadingRef(true);
					}
					const response = await requestGQL.getRequestTypes({
						list_all: true
					});
					this.requestTypes = response.data || [];
				} catch (error) {
					this.$helper.notifyError({ context: this, error });
				} finally {
					if (loadingRef) {
						loadingRef(false);
					}
				}
			}
		}
	},
	created() {
		this.getRequestStatusCount();
	}
};
</script>

<style lang="scss" scoped>
.request-filter {
	.request-filter__item {
		&:not(:last-child) {
			margin-bottom: 16px;
		}

		::v-deep .vs__dropdown-menu {
			top: initial;
		}
	}
}
</style>
